import {observer} from 'mobx-react-lite'
import React, {useLayoutEffect, useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Button} from '@components/common/base/buttons'
import {Loader} from '@components/common/base/loader'
import {showConfirm} from '@components/common/base/notifications'
import {Popup} from '@components/common/base/popup'
import {useForceUpdate, useMemoOne} from '@containers/hooks'
import {
  CompanyInvitePopupContext,
  CompanyInvitePopupStore,
  InviteFrom,
  useCompanyInvitePopupStore
} from './company-invite-popup-store'
import {EmailsInputWithErrors} from './emails-input-with-errors'
import {ProjectList} from './project-list'

const TwContent = tw.div`
  flex
  flex-col
  pt-8
  pb-24
  gap-8
  h-full
`

const TwCaption = tw.div`
  text-primary
  h2-semibold
  cursor-default
`

interface Props {
  close(): void
  inviteFrom: InviteFrom
}

interface FooterProps {
  close(): void
  submit(): void
  canSubmit(): boolean
  cancelDisabled?: boolean
}

const Footer = observer((props: FooterProps) => {
  const {submit, close, canSubmit, cancelDisabled} = props
  return (
    <div className="flex items-center justify-end gap-12">
      <Button
        color="secondary"
        size="medium"
        onClick={close}
        disabled={cancelDisabled}
      >
        {Lang.msg.cancel}
      </Button>
      <Button
        color="primary"
        size="medium"
        onClick={submit}
        disabled={!canSubmit()}
      >
        {Lang.msg.invitePopup.inviteUsersButton}
      </Button>
    </div>
  )
})

export const CompanyInvitePopup = (props: Props) => {
  const store = useMemoOne(
    () => new CompanyInvitePopupStore(props.inviteFrom),
    []
  )

  useLayoutEffect(() => {
    store.syncProjects()
    store.syncCommon()
  }, [])

  return (
    <CompanyInvitePopupContext.Provider value={store}>
      <CompanyInvitePopupBase {...props} />
    </CompanyInvitePopupContext.Provider>
  )
}

const CompanyInvitePopupBase = observer((props: Props) => {
  const [, store] = useCompanyInvitePopupStore()
  const wait = useRef(false)
  const forceUpdate = useForceUpdate()

  const onClose = () => {
    if (store.emails.length) {
      void showConfirm(Lang.msg.proj.unsavedChangesHint).then(data => {
        data && props.close()
      })
    } else {
      props.close()
    }
  }

  const onSubmit = async () => {
    if (wait.current) {
      return
    }

    wait.current = true
    forceUpdate()

    await store.onSubmit()

    wait.current = false
    forceUpdate()
    props.close()
  }

  return (
    <Popup
      close={onClose}
      title={Lang.msg.invitePopup.inviteToCompany}
      width={600}
      minHeight={500}
      autoHeight
      noScroll={true}
      footer={
        <Footer
          close={props.close}
          submit={onSubmit}
          canSubmit={store.canSubmit}
          cancelDisabled={wait.current}
        />
      }
    >
      <TwContent>
        <TwCaption>{Lang.msg.invitePopup.emailList}</TwCaption>
        <div className="pb-8">
          {wait.current ? (
            <div className="min-h-128 flex items-center justify-center">
              <Loader />
            </div>
          ) : (
            <EmailsInputWithErrors
              size="medium"
              esc={onClose}
              enter={() => {
                if (store.canSubmit()) {
                  void onSubmit()
                }
              }}
            />
          )}
        </div>

        <TwCaption>{Lang.msg.invitePopup.inviteInProjects}</TwCaption>
        <ProjectList />
      </TwContent>
    </Popup>
  )
})
